// File: src/components/JobDashboard.js

import React, { useEffect, useState } from 'react';
import { fetchJobs } from './jobsService';
import { useNavigate, useSearchParams } from 'react-router-dom';
import DashboardLayout from '../../layouts/DashboardLayout';
import { FaEye, FaEdit, FaTrash, FaCheck, FaLink, FaFileInvoiceDollar } from 'react-icons/fa';
import Calendar from 'react-calendar';
import 'react-calendar/dist/Calendar.css';
import styled from 'styled-components';
import Modal from '../../components/shared/Modal';
import { deleteJob } from './jobsService';
import ScheduledJobs from './ScheduledJobs';

const DashboardContainer = styled.div`
  padding: 20px;
  max-width: 1200px;
  margin: 0 auto;
  background-color: ${(props) => props.theme.colors.background};
  color: #000000; /* Replace with your theme's text color */
`;

const Row = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  margin-bottom: 20px;

  @media (min-width: 768px) {
    flex-direction: row;
  }
`;

const Card = styled.div`
position: relative; /* Add this line */
color: #ffffff;
 background-color: ${(props) => props.theme.colors.surface};
  border-radius: ${(props) => props.theme.borderRadius}; /* Use theme variable */
  padding: 20px;
  flex: 1;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
`;

const SearchBar = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-bottom: 20px;
  background-color: ${(props) => props.theme.colors.surface};

  @media (min-width: 768px) {
    flex-direction: row;
    flex-wrap: wrap; // Allow items to wrap on larger screens if needed
  }
`;

const Input = styled.input`
  flex: 1;
  padding: 15px;
  border-radius: ${(props) => props.theme.borderRadius}; /* Use theme variable */
  border: 1px solid ${(props) => props.theme.colors.inputGray};
  background-color: ${(props) => props.theme.colors.surface};
  color: ${(props) => props.theme.colors.textPrimary};
  font-size: 16px;
  min-width: 150px; 

  &::placeholder {
    color: #888;
  }
`;

const DateInput = styled(Input).attrs({ type: 'date' })`
  min-width: 180px; 
`;


const Select = styled.select`
  padding: 15px;
  border-radius: ${(props) => props.theme.borderRadius}; /* Use theme variable */
  border: 1px solid ${(props) => props.theme.colors.inputGray};
  background-color: ${(props) => props.theme.colors.surface};
  color: ${(props) => props.theme.colors.textPrimary};
  font-size: 16px;
  min-width: 150px; 
`;

const Button = styled.button`
  padding: 15px;
  border-radius: ${(props) => props.theme.borderRadius}; /* Use theme variable */
  border: none;
  background-color: ${(props) => props.theme.colors.primary};
  color: ${(props) => props.theme.colors.background};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
  font-size: 16px;
  font-weight: bold;
  transition: background-color 0.3s;

  &:hover {
    background-color: ${(props) => props.theme.colors.background};
    color: ${(props) => props.theme.colors.primary};
  }
`;

const Tabs = styled.div`
  display: flex;
  flex-wrap: wrap;
  margin-bottom: 20px;
`;

const Tab = styled.button`
  padding: 10px 15px;
  margin-right: 5px;
  border: 1px solid ${(props) => props.theme.colors.surface};
  background-color: ${(props) => (props.active ? props.theme.colors.primary : props.theme.colors.background)};
  color: ${(props) => (props.active ? props.theme.colors.black : props.theme.colors.primary)}; 
  cursor: pointer;
  font-size: 16px;
  border-radius: ${(props) => props.theme.borderRadius}; /* Use theme variable */

  &:hover {
    background-color: ${(props) => props.theme.colors.primary};
  color: ${(props) => props.theme.colors.black};
  }
`;

const JobList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 15px;
`;

const JobItem = styled.div`
  background-color: ${(props) => props.theme.colors.background};
  border-radius: ${(props) => props.theme.borderRadius}; /* Use theme variable */
  padding: ${(props) => props.theme.spacing(2) || '16px'};
  transition: background-color 0.3s;

  &:hover {
    background-color: ${(props) => props.theme.colors.surface};
  }
`;

const JobHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${(props) => props.theme.spacing(1) || '8px'}; 

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    margin-bottom: ${(props) => props.theme.spacing(1.5) || '12px'};
  }
`;

const JobName = styled.h3`
  margin: 0;
  font-size: 18px;
  color: ${(props) => props.theme.colors.textPrimary};

  @media (max-width: 768px) {
    font-size: 16px; 
    margin-bottom: ${(props) => props.theme.spacing(1) || '8px'}; 
  }
`;

const ActionButtonsContainer = styled.div`
  display: flex;
  flex-wrap: wrap; 
  gap: ${(props) => props.theme.spacing(0.5) || '4px'}; 

  @media (max-width: 768px) {
    justify-content: flex-start; 
    width: 100%; 
  }
`;

const ActionButton = styled.button`
  background: none;
  border: none;
  color: ${(props) => props.theme.colors.primary};
  cursor: pointer;
  margin: ${(props) => props.theme.spacing(0.5) || '4px'}; 
  padding: ${(props) => props.theme.spacing(1) || '8px'}; 
  font-size: 16px; 
  transition: color 0.3s;
  display: inline-flex; 
  align-items: center;

  &:hover {
    color: ${(props) => props.theme.colors.textPrimary};
  }

  @media (max-width: 768px) {
    font-size: 18px; 
    padding: ${(props) => props.theme.spacing(0.75) || '6px'};
  }
`;

const JobDetails = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: ${(props) => props.theme.spacing(1.5) || '12px'};
  color: ${(props) => props.theme.colors.textSecondary};

  @media (max-width: 768px) {
    grid-template-columns: 1fr; 
    gap: ${(props) => props.theme.spacing(1) || '8px'};
  }
`;

const JobDetail = styled.div`
  font-size: 14px;
  word-break: break-word; 
`;


const Pagination = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 20px;
`;

const PageButton = styled.button`
  padding: 5px 10px;
  margin: 0 5px;
  border: 1px solid #ccc;
  background-color: ${(props) => props.theme.colors.primary};
  color: ${(props) => props.theme.colors.surface};
  cursor: pointer;
  transition: background-color 0.3s;
  border-radius: ${(props) => props.theme.borderRadius}; /* Use theme variable */

  &:hover {
    background-color: #f1f1f1; 
  }

  &.active {
    background-color: ${(props) => props.theme.colors.textSecondary};
    color: ${(props) => props.theme.colors.background};
  }
`;

const StyledCalendar = styled(Calendar)`
  width: 100%;
  background-color: ${(props) => props.theme.colors.background};
  color: ${(props) => props.theme.colors.primary};
  border: none;
  border-radius: ${(props) => props.theme.borderRadius}; /* Use theme variable */

  .react-calendar{
    width: 100%;
    border-radius: ${(props) => props.theme.borderRadius}; /* Ensure inner calendar also gets radius */
  }

  .react-calendar__navigation{
    background-color: ${(props) => props.theme.colors.primary};
    border-top-left-radius: ${(props) => props.theme.borderRadius};
    border-top-right-radius: ${(props) => props.theme.borderRadius};
  }
  .react-calendar__navigation__arrow{
    color: ${(props) => props.theme.colors.surface};
  }

  .react-calendar__navigation__label{
    color: ${(props) => props.theme.colors.surface};
    
    &:hover{
      background-color: ${(props) => props.theme.colors.surface};
    } 
    &.active{
      background-color: ${(props) => props.theme.colors.surface};
    } 
  }

  .react-calendar__tile {
    padding: 10px;
    background-color: ${(props) => props.theme.colors.surface};
    color: ${(props) => props.theme.colors.textPrimary};
    height: auto;
    min-height: 80px;
    border: 1px solid ${(props) => props.theme.colors.background};
    /* Individual tile radius can be tricky with borders, usually keep them square or slight radius if no border issues */
  }

  .react-calendar__tile--now {
    background-color: ${(props) => props.theme.colors.background};
  }

  .react-calendar__month-view__days__day{
    &:hover{
      background-color: ${(props) => props.theme.colors.background};
    }
    &.active{
      background-color: ${(props) => props.theme.colors.surface};
    }
  }
  .react-calendar__month-view__days__day--weekend {
    color: ${(props) => props.theme.colors.textSecondary};
  }

  .react-calendar__tile--active {
    background-color: ${(props) => props.theme.colors.background} !important; 
    color: ${(props) => props.theme.colors.primary}
  }
`;

const getEventTypeColor = (theme, eventType) => {
    switch (eventType) {
        case 'pastConcluded':
            return theme.colors.textSecondary || '#CCCCCC'; 
        case 'sameDay':
            return theme.colors.success || '#84c959'; 
        case 'scheduledFuture':
            return theme.colors.primary || '#F69620'; 
        case 'pastNotConcluded':
            return theme.colors.error || '#FF5252'; 
        default:
            return theme.colors.primary || '#F69620'; 
    }
};

const JobEvent = styled.div`
  font-size: 12px;
  padding: 2px 4px;
  margin: 2px 0;
  border-radius: 2px; /* Keep small radius for events */
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  text-align: left;
  background-color: ${(props) => getEventTypeColor(props.theme, props.eventType)};
  color: ${(props) => props.theme.colors.textPrimary || '#ffffff'}; 
`;

const Tooltip = styled.div`
  position: absolute;
  top: ${(props) => props.y}px;
  left: ${(props) => props.x}px;
  transform: translate(-50%, -100%); 
  background-color: #ffffff;
  color: #000000;
  padding: 10px;
  border: 1px solid #ccc;
  border-radius: ${(props) => props.theme.borderRadius}; /* Use theme variable */
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  z-index: 1000;
  white-space: nowrap;
  pointer-events: none; 
`;

const TooltipLink = styled.div`
  color: ${props => props.theme.colors.primary || '#F69620'};
  cursor: pointer;
  text-decoration: underline;
  display: block; 
  margin-top: 5px;
  &:hover {
    color: ${props => props.theme.colors.textPrimary || '#000000'}; 
  }
`;


const CardWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${(props) => props.theme.spacing(3)};
  margin-top: ${(props) => props.theme.spacing(1)};
  margin-bottom: ${(props) => props.theme.spacing(2)};
`;

const today = new Date();
today.setHours(0, 0, 0, 0);

const JobDashboard = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [jobs, setJobs] = useState([]);
  const [searchTerm, setSearchTerm] = useState(() => searchParams.get('search') || '');
  const [filterStatus, setFilterStatus] = useState(() => searchParams.get('status') || 'All');
  const [currentPage, setCurrentPage] = useState(() => {
    const p = parseInt(searchParams.get('page'), 10);
    return Number.isFinite(p) && p > 0 ? p : 1;
  });
  const jobsPerPage = 5;
  const [activeTab, setActiveTab] = useState(() => searchParams.get('tab') || 'scheduled');
  const [tooltipVisible, setTooltipVisible] = useState(false);
  const [tooltipContent, setTooltipContent] = useState(null);
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });

  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [jobToDelete, setJobToDelete] = useState(null);
  const [addressLatLng, setAddressLatLng] = useState('');

  const [filterDates, setFilterDates] = useState(() => ({
    startDate: searchParams.get('from') || '',
    endDate: searchParams.get('to') || '',
  }));

  useEffect(() => {
    const next = {};
    if (searchTerm) next.search = searchTerm;
    if (filterStatus && filterStatus !== 'All') next.status = filterStatus;
    if (activeTab && activeTab !== 'scheduled') next.tab = activeTab;
    if (filterDates.startDate) next.from = filterDates.startDate;
    if (filterDates.endDate) next.to = filterDates.endDate;
    if (currentPage > 1) next.page = String(currentPage);
    setSearchParams(next, { replace: true });
  }, [searchTerm, filterStatus, activeTab, filterDates, currentPage, setSearchParams]);

  const handleNavClick = (e, to) => {
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    if (e.button !== undefined && e.button !== 0) return;
    e.preventDefault();
    navigate(to);
  };

  useEffect(() => {
    const getJobs = async () => {
      try {
        const data = await fetchJobs();
        setJobs(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error(err);
        setJobs([]); 
      }
    };
    setAddressLatLng({ lat: parseFloat(42.45562), lng: parseFloat(-71.46752) });
    getJobs();
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (tooltipVisible && !event.target.closest('.job-event-tooltip-trigger') && !event.target.closest('.tooltip-popup')) {
        setTooltipVisible(false);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [tooltipVisible]);

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setCurrentPage(1); 
  };

  const handleDateChange = (value, type) => {
    setFilterDates(prev => ({ ...prev, [type]: value }));
    setCurrentPage(1);
  };

  const filteredJobs = jobs.filter((job) => {
    let statusMatch = true;
    switch (activeTab) {
      case 'scheduled':
        statusMatch = job.status === 'Scheduled' && 
                      job.scheduled_end_date && new Date(job.scheduled_end_date) > today;
        break;
      case 'sameDay':
        statusMatch =
          job.status === 'Scheduled' &&
          job.scheduled_start_date && job.scheduled_end_date &&
          new Date(job.scheduled_start_date).toDateString() === new Date(job.scheduled_end_date).toDateString() &&
          new Date(job.scheduled_start_date) >= today;
        break;
      case 'pastNotConcluded':
        statusMatch =
          job.status !== 'Completed' && job.scheduled_end_date && new Date(job.scheduled_end_date) < today;
        break;
      case 'pastConcluded':
        statusMatch = job.status === 'Completed';
        break;
      default:
        statusMatch = true;
    }

    const searchMatch = (
        (job.customer_name && job.customer_name.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (job.job_city && job.job_city.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (job.services && job.services.some((service) =>
            service.service_name && service.service_name.toLowerCase().includes(searchTerm.toLowerCase())
        ))
    );

    const statusFilterMatch = filterStatus === 'All' || job.status === filterStatus;

    let dateFilterMatch = true;
    if (job.scheduled_start_date) { 
        const jobStartDate = new Date(job.scheduled_start_date);
        jobStartDate.setHours(0,0,0,0); 

        if (filterDates.startDate) {
            const startDateFilter = new Date(filterDates.startDate);
            startDateFilter.setHours(0,0,0,0); 
            if (jobStartDate < startDateFilter) {
                dateFilterMatch = false;
            }
        }
        if (dateFilterMatch && filterDates.endDate) {
            const endDateFilter = new Date(filterDates.endDate);
            endDateFilter.setHours(23,59,59,999); 
            if (jobStartDate > endDateFilter) { 
                dateFilterMatch = false;
            }
        }
    } else if (filterDates.startDate || filterDates.endDate) { 
        dateFilterMatch = false;
    }

    return statusMatch && searchMatch && statusFilterMatch && dateFilterMatch;
  });

  const indexOfLastJob = currentPage * jobsPerPage;
  const indexOfFirstJob = indexOfLastJob - jobsPerPage;
  const currentJobs = filteredJobs.slice(indexOfFirstJob, indexOfLastJob);
  const totalPages = Math.ceil(filteredJobs.length / jobsPerPage);

  const pageNumbers = [];
  for (let i = 1; i <= totalPages; i++) {
    pageNumbers.push(i);
  }

  const tileContent = ({ date, view }) => {
    if (view === 'month') {
      const jobsForDate = jobs.filter((job) => {
        if (!job.scheduled_start_date || !job.scheduled_end_date) return false;
        const jobStartDate = new Date(job.scheduled_start_date);
        jobStartDate.setHours(0,0,0,0);
        const jobEndDate = new Date(job.scheduled_end_date);
        jobEndDate.setHours(0,0,0,0);
        const dataCheck = new Date(date); 
        dataCheck.setHours(0,0,0,0);
        return dataCheck >= jobStartDate && dataCheck <= jobEndDate;
      });

      if (jobsForDate.length > 0) {
        return (
          <div>
            {jobsForDate.map((job) => {
              let eventType = 'scheduled'; 
              if (job.scheduled_start_date && job.scheduled_end_date) {
                  const jobStartDateObj = new Date(job.scheduled_start_date);
                  jobStartDateObj.setHours(0,0,0,0);
                  const jobEndDateObj = new Date(job.scheduled_end_date);
                  jobEndDateObj.setHours(0,0,0,0);

                  if (job.status === 'Completed' && jobEndDateObj < today) {
                    eventType = 'pastConcluded';
                  } else if (jobStartDateObj.toDateString() === jobEndDateObj.toDateString() && jobStartDateObj >= today) {
                    eventType = 'sameDay';
                  } else if (jobStartDateObj >= today) { 
                    eventType = 'scheduledFuture';
                  } else if (jobEndDateObj < today && job.status !== 'Completed') { 
                    eventType = 'pastNotConcluded';
                  }
              }
              
              return (
                <JobEvent
                  key={job.job_id}
                  eventType={eventType}
                  onClick={(e) => handleJobEventClick(e, job)}
                  className="job-event-tooltip-trigger" 
                >
                  {job.customer_name} - {job.job_city}
                </JobEvent>
              );
            })}
          </div>
        );
      }
    }
    return null;
  };

  const handleJobEventClick = (e, job) => {
    e.stopPropagation(); 
    const calendarRect = e.target.closest('.react-calendar').getBoundingClientRect();
    let x = e.clientX - calendarRect.left + e.target.offsetWidth / 2; 
    let y = e.clientY - calendarRect.top; 


    if (x + 150 > calendarRect.width) { 
        x = calendarRect.width - 150;
    }
    if (x < 0) {
        x = 10;
    }
    if (y - 100 < 0) { 
        y = y + e.target.offsetHeight + 10; 
    } else {
        y = y - 10; 
    }


    setTooltipContent(
      <>
        <strong>{job.customer_name || 'N/A'}</strong> ({job.job_city || 'N/A'})<br />
        Services: {job.services?.map(s => s.service_name).join(', ') || 'N/A'}<br />
        Start: {job.scheduled_start_date ? new Date(job.scheduled_start_date).toLocaleDateString() : 'N/A'}<br />
        End: {job.scheduled_end_date ? new Date(job.scheduled_end_date).toLocaleDateString() : 'N/A'}<br />
        Status: {job.status || 'N/A'}<br />
        <TooltipLink
          as="a"
          href={`/jobs/${job.job_id}`}
          onClick={(e) => {
            if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || (e.button && e.button !== 0)) return;
            e.preventDefault();
            navigate(`/jobs/${job.job_id}`);
            setTooltipVisible(false);
          }}
        >
          View Job Details
        </TooltipLink>
        {job.lead_id && (
          <TooltipLink
            as="a"
            href={`/leads/view/${job.lead_id}`}
            onClick={(e) => {
              if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || (e.button && e.button !== 0)) return;
              e.preventDefault();
              navigate(`/leads/view/${job.lead_id}`);
              setTooltipVisible(false);
            }}
          >
            View Lead
          </TooltipLink>
        )}
        {job.estimate_id && (
          <TooltipLink
            as="a"
            href={`/estimates/view/${job.estimate_id}`}
            onClick={(e) => {
              if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || (e.button && e.button !== 0)) return;
              e.preventDefault();
              navigate(`/estimates/view/${job.estimate_id}`);
              setTooltipVisible(false);
            }}
          >
            View Estimate
          </TooltipLink>
        )}
      </>
    );
    setTooltipPosition({ x, y });
    setTooltipVisible(true);
  }; 

  const handleDeleteClick = (job) => {
    setJobToDelete(job);
    setIsDeleteModalOpen(true);
  };

  const deleteJobConfirm = async () => {
    try {
      await deleteJob(jobToDelete.job_id);
      setJobs(jobs.filter(job => job.job_id !== jobToDelete.job_id));
      setIsDeleteModalOpen(false);
      setJobToDelete(null);
    } catch (error) {
      console.error('Error deleting job:', error);
    }
  };

  return (
    <DashboardLayout>
      <DashboardContainer>
        <Row>
          <Card>
            <SearchBar>
              <Input
                type="text"
                placeholder="Search jobs..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              <DateInput
                value={filterDates.startDate}
                onChange={(e) => handleDateChange(e.target.value, 'startDate')}
                title="Filter by start date"
              />
              <DateInput
                value={filterDates.endDate}
                onChange={(e) => handleDateChange(e.target.value, 'endDate')}
                title="Filter by end date"
              />
              <Select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
              >
                <option value="All">All Status</option>
                <option value="Scheduled">Scheduled</option>
                <option value="In Progress">In Progress</option>
                <option value="Completed">Completed</option>
              </Select>
              <Button onClick={() => navigate('/jobs/new')}>
                Add Job
              </Button>
            </SearchBar>

            <Tabs>
              <Tab
                active={activeTab === 'scheduled'}
                onClick={() => handleTabChange('scheduled')}
              >
                Scheduled Jobs
              </Tab>
              <Tab
                active={activeTab === 'sameDay'}
                onClick={() => handleTabChange('sameDay')}
              >
                Same Day Jobs
              </Tab>
              <Tab
                active={activeTab === 'pastNotConcluded'}
                onClick={() => handleTabChange('pastNotConcluded')}
              >
                Past (Not Concluded)
              </Tab>
              <Tab
                active={activeTab === 'pastConcluded'}
                onClick={() => handleTabChange('pastConcluded')}
              >
                Past (Concluded)
              </Tab>
            </Tabs>

            <JobList>
              {currentJobs.map((job) => (
                <JobItem key={job.job_id}>
                  <JobHeader>
                    <JobName>{job.customer_name}</JobName>
                    <ActionButtonsContainer>
                      {job.lead_id && (
                        <ActionButton
                          as="a"
                          href={`/leads/view/${job.lead_id}`}
                          title="View Lead — Cmd/Ctrl-click or middle-click to open in a new tab"
                          onClick={(e) => handleNavClick(e, `/leads/view/${job.lead_id}`)}
                        >
                          <FaLink />
                        </ActionButton>
                      )}
                      {job.estimate_id && (
                        <ActionButton
                          as="a"
                          href={`/estimates/view/${job.estimate_id}`}
                          title="View Estimate — Cmd/Ctrl-click or middle-click to open in a new tab"
                          onClick={(e) => handleNavClick(e, `/estimates/view/${job.estimate_id}`)}
                        >
                          <FaFileInvoiceDollar />
                        </ActionButton>
                      )}
                      <ActionButton
                        as="a"
                        href={`/jobs/${job.job_id}`}
                        title="View Job — Cmd/Ctrl-click or middle-click to open in a new tab"
                        onClick={(e) => handleNavClick(e, `/jobs/${job.job_id}`)}
                      >
                        <FaEye />
                      </ActionButton>
                      <ActionButton
                        as="a"
                        href={`/jobs/edit/${job.job_id}`}
                        title="Edit Job — Cmd/Ctrl-click or middle-click to open in a new tab"
                        onClick={(e) => handleNavClick(e, `/jobs/edit/${job.job_id}`)}
                      >
                        <FaEdit />
                      </ActionButton>
                      <ActionButton title="Delete Job" onClick={() => handleDeleteClick(job)}>
                        <FaTrash />
                      </ActionButton>
                      
                      {job.scheduled_end_date && new Date(job.scheduled_end_date) < new Date() && 
                        job.status !== 'Completed' && (
                          <ActionButton
                            title="Mark as Completed"
                            onClick={() => {
                              /* Complete job logic */
                            }}
                          >
                            <FaCheck />
                          </ActionButton>
                        )}
                    </ActionButtonsContainer>
                  </JobHeader>
                  <JobDetails>
                    <JobDetail>
                      <strong>City:</strong> {job.job_city}
                    </JobDetail>
                    <JobDetail>
                      <strong>Services:</strong>{' '}
                      {job.services && job.services
                        .map((service) => service.service_name)
                        .join(', ')}
                    </JobDetail>
                    <JobDetail>
                      <strong>Start:</strong>{' '}
                      {job.scheduled_start_date ? new Date(job.scheduled_start_date).toLocaleDateString() : 'N/A'}
                    </JobDetail>
                    <JobDetail>
                      <strong>End:</strong>{' '}
                      {job.scheduled_end_date ? new Date(job.scheduled_end_date).toLocaleDateString() : 'N/A'}
                    </JobDetail>
                    <JobDetail>
                      <strong>Status:</strong> {job.status}
                    </JobDetail>
                  </JobDetails>
                </JobItem>
              ))}
            </JobList>

            <Pagination>
              {pageNumbers.map((number) => (
                <PageButton
                  key={number}
                  className={currentPage === number ? 'active' : ''}
                  onClick={() => setCurrentPage(number)}
                >
                  {number}
                </PageButton>
              ))}
            </Pagination>
          </Card>
        </Row>

        {/* Calendar Card */}
        <Row>
          <Card>
          <StyledCalendar onClickDay={(value, event) => { 
                if (tooltipVisible && !event.target.closest('.job-event-tooltip-trigger')) {
                     setTooltipVisible(false);
                }
              }} 
              tileContent={tileContent}
            />
            {tooltipVisible && (
              <Tooltip x={tooltipPosition.x} y={tooltipPosition.y} className="tooltip-popup"> 
                {tooltipContent}
              </Tooltip>
            )}
          </Card>
        </Row>

        <Row>
            <Card>
              <ScheduledJobs currentJobLatLng={addressLatLng} /> 
            </Card>
        </Row>
      </DashboardContainer>
      <Modal
        isOpen={isDeleteModalOpen}
        title="Confirm Deletion"
        message={`Are you sure you want to delete the job for ${jobToDelete?.customer_name}?`}
        onConfirm={deleteJobConfirm}
        onCancel={() => setIsDeleteModalOpen(false)}
      />
    </DashboardLayout>
  );
};

export default JobDashboard;
